import React, { useEffect, useState, useMemo, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  CircularProgress, Grid, Box, Button, TextField, InputAdornment, Typography,
  Avatar, Chip, IconButton, Tooltip, Checkbox, alpha, Skeleton,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import { useDeleteShopMutation, useGetShopQuery } from "../../../Api/attoDeskApi";
import { IShop } from "../../../Api/Interface/api.interface";
import NewPopUpCustomer from "../Edit/index";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import { useNotifier } from "../../../Core/Notifier";
import SearchIcon from "@mui/icons-material/Search";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditIcon from "@mui/icons-material/Edit";
import VisibilityIcon from "@mui/icons-material/Visibility";
import ReceiptIcon from "@mui/icons-material/Receipt";
import BlockIcon from "@mui/icons-material/Block";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import StorefrontIcon from "@mui/icons-material/Storefront";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import AltRouteIcon from "@mui/icons-material/AltRoute";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import GroupsIcon from "@mui/icons-material/Groups";
import PageLoader from "../../../Components/Loader/PageLoader";
import Pagination from "../../../Components/Pagination";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const API_URL = (process.env.REACT_APP_API_URL || "").trim();
const LARAVEL_API_URL = (process.env.REACT_APP_LARAVEL_API || "").trim();

// DCC navy + gold palette
const NAVY = "#0B1E2D";
const NAVY_DARK = "#071521";
const GOLD = "#D4AF37";
const GOLD_SOFT = "#F5D97E";
const PAGE_BG = "#F8FAFC";
const TEXT_PRIMARY = "#1F2937";
const TEXT_SECONDARY = "#6B7280";
const BORDER = "#E5E7EB";
const ACCENT = NAVY;

interface EnrichedShop extends IShop {
  order_count: number;
  last_order_date: string | null;
  outstanding: number;
  credit_status: {
    credit_limit: number; outstanding: number; available: number | null;
    usage_percent: number; color: string; is_blocked: boolean;
    has_overdue: boolean; manually_blocked: boolean; credit_terms: string;
    overdue_count?: number; invoice_counts?: { total: number; unpaid: number; partial: number; paid: number };
  };
}

const TABS = [
  { key: "all", label: "All", color: NAVY },
  { key: "active", label: "Active", color: "#059669" },
  { key: "overdue", label: "Overdue", color: "#ea580c" },
  { key: "blocked", label: "Blocked", color: "#dc2626" },
  { key: "no_orders", label: "No Orders", color: "#64748b" },
];

type SortKey = "name" | "outstanding" | "order_count" | "last_order" | "credit_limit";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const [deleteCategory] = useDeleteShopMutation();
  const LARAVEL_URL = LARAVEL_API_URL;
  const { showErrorMessage, showMessage } = useNotifier();

  const [shops, setShops] = useState<EnrichedShop[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [page, setPage] = useState(0);
  const [rowsPerPage] = useState(50);
  // Default sort: alphabetical A→Z by restaurant name (matches backend order).
  const [sortKey, setSortKey] = useState<SortKey | null>("name");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("asc");
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const [selectedCoursing, setSelectedCoursing] = useState<IShop | null>(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IShop | null>(null);

  const [routes, setRoutes] = useState<{ id: number; name: string }[]>([]);
  const [routeFilter, setRouteFilter] = useState("all");

  const getHeaders = () => {
    const t = localStorage.getItem("token") || "";
    return { "Content-Type": "application/json", Accept: "application/json", Authorization: `Bearer ${t}` };
  };

  const loadShops = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/shops/list-enriched`, { method: "POST", headers: getHeaders() });
      const data = await res.json();
      if (data.success && data.data) { setShops(data.data); }
      else {
        const fallback = await fetch(`${API_URL}/shop/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ searchText: "", id: "" }) });
        const fbData = await fallback.json();
        if (fbData.data) {
          setShops(fbData.data.map((s: any) => ({ ...s, order_count: 0, last_order_date: null, outstanding: 0, credit_status: { credit_limit: 0, outstanding: 0, available: null, usage_percent: 0, color: "green", is_blocked: false, has_overdue: false, manually_blocked: false, credit_terms: "no_limit", invoice_counts: { total: 0, unpaid: 0, partial: 0, paid: 0 } } })));
        }
      }
    } catch { showErrorMessage("Failed to load restaurants"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadShops(); }, [loadShops]);

  useEffect(() => {
    fetch(`${API_URL}/route/getAll`, { method: "POST", headers: getHeaders(), body: JSON.stringify({ searchText: "" }) })
      .then(r => r.json()).then(json => { if (json.data) setRoutes(json.data.map((r: any) => ({ id: r.id, name: r.name }))); }).catch(() => {});
  }, []);

  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) { showMessage("Deleted successfully"); setOpenDeleteCategory(false); loadShops(); }
      else showErrorMessage("Failed to delete");
    } catch { showErrorMessage("Failed to delete"); }
  };

  const handleBlockToggle = async (shopId: number, block: boolean) => {
    try {
      const res = await fetch(`${API_URL}/shops/${shopId}/credit-settings`, { method: "PUT", headers: getHeaders(), body: JSON.stringify({ credit_blocked: block }) });
      const data = await res.json();
      if (data.success) { showMessage(block ? "Restaurant blocked" : "Restaurant unblocked"); loadShops(); }
    } catch { showErrorMessage("Failed"); }
  };

  const filtered = useMemo(() => {
    let list = shops;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      list = list.filter(s =>
        (s.customerCompanyName || "").toLowerCase().includes(q) ||
        (s.customerFirstName || "").toLowerCase().includes(q) ||
        (s.customerLastName || "").toLowerCase().includes(q) ||
        (s.customerMobileNum || "").toLowerCase().includes(q) ||
        (s.customerCompanyPhoneNumebr || "").toLowerCase().includes(q) ||
        (s.customerEmail || "").toLowerCase().includes(q) ||
        (s.customerCompanyEmail || "").toLowerCase().includes(q)
      );
    }
    if (statusFilter === "all") list = list.filter(s => !s.credit_status?.is_blocked);
    else if (statusFilter === "active") list = list.filter(s => !s.credit_status?.is_blocked && !s.credit_status?.has_overdue);
    else if (statusFilter === "overdue") list = list.filter(s => s.credit_status?.has_overdue && !s.credit_status?.is_blocked);
    else if (statusFilter === "blocked") list = list.filter(s => s.credit_status?.is_blocked);
    else if (statusFilter === "outstanding") list = list.filter(s => (s.outstanding || 0) > 0);
    else if (statusFilter === "no_orders") list = list.filter(s => s.order_count === 0 && !s.credit_status?.is_blocked);
    if (routeFilter === "unassigned") {
      list = list.filter(s => { const rids = typeof s.route_ids === "string" ? JSON.parse(s.route_ids || "[]") : (s.route_ids || []); return !rids || rids.length === 0; });
    } else if (routeFilter !== "all") {
      const rid = Number(routeFilter);
      list = list.filter(s => { const rids = typeof s.route_ids === "string" ? JSON.parse(s.route_ids || "[]") : (s.route_ids || []); return Array.isArray(rids) && rids.includes(rid); });
    }
    return list;
  }, [shops, searchQuery, statusFilter, routeFilter]);

  const sorted = useMemo(() => {
    // No sort selected → keep backend order (created_at DESC = newest first).
    if (!sortKey) return filtered;
    return [...filtered].sort((a, b) => {
      let va: any, vb: any;
      switch (sortKey) {
        case "name": va = (a.customerCompanyName || "").toLowerCase(); vb = (b.customerCompanyName || "").toLowerCase(); break;
        case "outstanding": va = a.outstanding; vb = b.outstanding; break;
        case "order_count": va = a.order_count; vb = b.order_count; break;
        case "last_order": va = a.last_order_date || ""; vb = b.last_order_date || ""; break;
        case "credit_limit": va = a.credit_status?.credit_limit || 0; vb = b.credit_status?.credit_limit || 0; break;
        default: va = 0; vb = 0;
      }
      if (va < vb) return sortDir === "asc" ? -1 : 1;
      if (va > vb) return sortDir === "asc" ? 1 : -1;
      return 0;
    });
  }, [filtered, sortKey, sortDir]);

  const toggleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  };

  const SortIcon = ({ k }: { k: SortKey }) => {
    if (sortKey !== k) return null;
    return sortDir === "asc" ? <ArrowUpwardIcon sx={{ fontSize: 12, ml: 0.25, color: GOLD }} /> : <ArrowDownwardIcon sx={{ fontSize: 12, ml: 0.25, color: GOLD }} />;
  };

  const handleExportExcel = () => {
    const rows = sorted.map((s: any) => ({
      "Restaurant Name": s.customerCompanyName || "", "Phone": s.customerCompanyPhoneNumebr || s.customerMobileNum || "",
      "Email": s.customerCompanyEmail || s.customerEmail || "", "Contact Person": `${s.customerFirstName || ""} ${s.customerLastName || ""}`.trim(),
      "Street #": s.customerStreetNumber || "", "Street Name": s.customerStreetName || "", "City": s.customerCity || "",
      "Province": s.customerProvince || "", "Postal Code": s.customerPostalCode || "",
      "Credit Balance": Number(s.credit_balance || 0).toFixed(2), "Credit Limit": Number(s.credit_limit || 0).toFixed(2),
      "Orders": s.order_count || 0, "Status": s.isDelete ? "Deleted" : "Active",
    }));
    const ws = XLSX.utils.json_to_sheet(rows);
    ws["!cols"] = Object.keys(rows[0] || {}).map(key => ({ wch: Math.max(key.length, ...rows.map(r => String((r as any)[key] || "").length)) + 2 }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Restaurants");
    XLSX.writeFile(wb, `Restaurants_${dayjs().format("YYYY-MM-DD")}.xlsx`);
  };

  const paged = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const counts = useMemo(() => ({
    total: shops.length,
    all: shops.filter(s => !s.credit_status?.is_blocked).length,
    active: shops.filter(s => !s.credit_status?.is_blocked && !s.credit_status?.has_overdue).length,
    overdue: shops.filter(s => s.credit_status?.has_overdue && !s.credit_status?.is_blocked).length,
    blocked: shops.filter(s => s.credit_status?.is_blocked).length,
    outstanding: shops.filter(s => (s.outstanding || 0) > 0).length,
    no_orders: shops.filter(s => s.order_count === 0 && !s.credit_status?.is_blocked).length,
  }), [shops]);

  const totalOutstanding = useMemo(() => shops.reduce((acc, s) => acc + (s.outstanding || 0), 0), [shops]);

  const toggleSelect = (id: number) => setSelected(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => { if (selected.size === paged.length) setSelected(new Set()); else setSelected(new Set(paged.map(s => s.id))); };

  // Skeleton rows are rendered inside the table while `loading` is true,
  // so the page chrome (header, KPIs, filters) stays mounted and stable.

  return (
    <Box sx={{ p: { xs: 1.5, md: 2.5 }, bgcolor: PAGE_BG, minHeight: "100vh" }}>

      {/* ═══ HEADER ═══ */}
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Box sx={{
              width: 44, height: 44, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center",
              background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
              boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
              border: `1px solid ${alpha(GOLD, 0.35)}`,
            }}>
              <StorefrontIcon sx={{ fontSize: 22, color: GOLD }} />
            </Box>
            <Box>
              <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">Restaurants</Typography>
              <Typography fontSize={12} color={TEXT_SECONDARY}>
                {counts.total} total restaurants managed
                <Box component="span" sx={{ ml: 1, color: GOLD, fontWeight: 700 }}>· Admin</Box>
              </Typography>
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
            <TextField size="small" placeholder="Search restaurants..."
              value={searchQuery} onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: TEXT_SECONDARY }} /></InputAdornment> }}
              sx={{ width: 220, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: BORDER }, "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) }, "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: "1.5px" } } }} />
            <TextField select size="small" value={routeFilter}
              onChange={(e) => { setRouteFilter(e.target.value); setPage(0); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><AltRouteIcon sx={{ fontSize: 16, color: TEXT_SECONDARY }} /></InputAdornment> }}
              sx={{ minWidth: 150, "& .MuiOutlinedInput-root": { borderRadius: 2.5, fontSize: 13, bgcolor: "#fff", "& fieldset": { borderColor: BORDER }, "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) }, "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: "1.5px" } } }}>
              <MuiMenuItem value="all">All Routes</MuiMenuItem>
              <MuiMenuItem value="unassigned">Unassigned</MuiMenuItem>
              {routes.map(r => <MuiMenuItem key={r.id} value={String(r.id)}>{r.name}</MuiMenuItem>)}
            </TextField>
            <Button variant="outlined" size="small" startIcon={<FileDownloadIcon sx={{ fontSize: 16 }} />} onClick={handleExportExcel}
              disabled={sorted.length === 0}
              sx={{
                borderRadius: 2.5, textTransform: "none", fontWeight: 600, fontSize: 12,
                borderColor: BORDER, color: TEXT_SECONDARY, bgcolor: "#fff",
                transition: "all 0.2s ease",
                "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) },
              }}>
              Export
            </Button>
            <Button variant="contained" color="secondary" size="small" disableElevation startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => navigate("/shop/create")}
              sx={{
                borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 12, px: 2.5,
                color: NAVY_DARK,
                background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
                boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
                transition: "all 0.2s ease",
                "&:hover": {
                  background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)`,
                  boxShadow: `0 6px 20px ${alpha(GOLD, 0.55)}`,
                  transform: "translateY(-1px)",
                },
              }}>
              Add Restaurant
            </Button>
          </Box>
        </Box>

        {/* ═══ KPI STRIP — clickable filters ═══ */}
        <Grid container spacing={2} sx={{ mb: 2.5 }}>
          {[
            { key: "all",          icon: <StorefrontIcon />,    label: "Total",       value: counts.total,       color: "#3b82f6", bg: "#eff6ff" },
            { key: "active",       icon: <CheckCircleIcon />,   label: "Active",      value: counts.active,      color: "#059669", bg: "#ecfdf5" },
            { key: "overdue",      icon: <WarningAmberIcon />,  label: "Overdue",     value: counts.overdue,     color: "#ea580c", bg: "#fff7ed" },
            { key: "blocked",      icon: <BlockIcon />,         label: "Blocked",     value: counts.blocked,     color: "#dc2626", bg: "#fef2f2" },
            { key: "outstanding",  icon: <TrendingUpIcon />,    label: "Outstanding", value: `$${totalOutstanding.toLocaleString("en-CA", { minimumFractionDigits: 2 })}`, color: "#7c3aed", bg: "#f5f3ff" },
          ].map((kpi) => {
            const isActive = statusFilter === kpi.key;
            return (
              <Grid item xs={6} sm={4} md={2.4} key={kpi.key}>
                <Box
                  role="button" tabIndex={0}
                  onClick={() => { setStatusFilter(kpi.key); setPage(0); }}
                  onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setStatusFilter(kpi.key); setPage(0); } }}
                  sx={{
                    p: 2, borderRadius: "12px", bgcolor: isActive ? "#FFFDF5" : "#fff",
                    border: `2px solid ${isActive ? NAVY : BORDER}`,
                    boxShadow: isActive
                      ? `0 8px 24px ${alpha(NAVY, 0.12)}, 0 0 0 4px ${alpha(kpi.color, 0.10)}`
                      : "0 4px 12px rgba(0,0,0,0.06)",
                    cursor: "pointer", outline: "none",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      boxShadow: "0 10px 24px rgba(11,30,45,0.10)",
                      borderColor: isActive ? NAVY : alpha(GOLD, 0.45),
                      transform: "translateY(-2px)",
                    },
                    "&:focus-visible": { boxShadow: `0 0 0 3px ${alpha(NAVY, 0.25)}` },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                    <Box sx={{ width: 32, height: 32, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: kpi.bg }}>
                      {React.cloneElement(kpi.icon, { sx: { fontSize: 16, color: kpi.color } })}
                    </Box>
                    <Typography fontSize={10} fontWeight={700} color={TEXT_SECONDARY} textTransform="uppercase" letterSpacing="0.06em">{kpi.label}</Typography>
                    {isActive && (
                      <Box sx={{ ml: "auto", width: 6, height: 6, borderRadius: "50%", bgcolor: kpi.color, boxShadow: `0 0 0 3px ${alpha(kpi.color, 0.18)}` }} />
                    )}
                  </Box>
                  <Typography fontSize={20} fontWeight={800} color={NAVY}>{kpi.value}</Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Active filter indicator */}
        {statusFilter !== "all" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5, fontSize: 12, color: TEXT_SECONDARY }}>
            <Typography fontSize={12} color={TEXT_SECONDARY}>Showing:</Typography>
            <Chip
              size="small"
              label={`${statusFilter[0].toUpperCase() + statusFilter.slice(1)} restaurants · ${sorted.length}`}
              onDelete={() => { setStatusFilter("all"); setPage(0); }}
              sx={{ fontSize: 11, fontWeight: 700, bgcolor: alpha(NAVY, 0.06), color: NAVY, border: `1px solid ${alpha(NAVY, 0.15)}` }}
            />
          </Box>
        )}

        {/* ═══ FILTER TABS ═══ */}
        <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap" }}>
          {TABS.map(t => {
            const count = (counts as any)[t.key] ?? 0;
            const active = statusFilter === t.key;
            return (
              <Chip key={t.key} label={`${t.label} (${count})`} onClick={() => { setStatusFilter(t.key); setPage(0); }} size="small"
                sx={{
                  fontSize: 11, fontWeight: 700, cursor: "pointer", borderRadius: 2, height: 30, px: 0.5,
                  transition: "all 0.2s ease",
                  ...(active
                    ? t.key === "all"
                      ? { bgcolor: NAVY, color: GOLD, boxShadow: `0 4px 12px ${alpha(NAVY, 0.35)}`, border: `1px solid ${alpha(GOLD, 0.4)}` }
                      : { bgcolor: t.color, color: "#fff", boxShadow: `0 2px 8px ${alpha(t.color, 0.3)}` }
                    : { bgcolor: "#fff", color: TEXT_SECONDARY, border: `1px solid ${BORDER}`, "&:hover": { bgcolor: alpha(GOLD, 0.08), borderColor: alpha(GOLD, 0.45), color: NAVY } }),
                }} />
            );
          })}
        </Box>

        {/* Bulk Actions */}
        {selected.size > 0 && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mt: 2, p: 2, bgcolor: alpha(NAVY, 0.04), borderRadius: 2.5, border: `1px solid ${alpha(GOLD, 0.25)}` }}>
            <Typography fontSize={13} fontWeight={700} color={NAVY}>{selected.size} selected</Typography>
            <Button size="small" variant="contained" disableElevation onClick={() => { Array.from(selected).forEach(id => handleBlockToggle(id, true)); setSelected(new Set()); }}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, fontWeight: 700, bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}>Block Selected</Button>
            <Button size="small" variant="outlined" onClick={() => setSelected(new Set())}
              sx={{ borderRadius: 2, textTransform: "none", fontSize: 11, borderColor: BORDER, color: TEXT_SECONDARY, "&:hover": { borderColor: GOLD, color: NAVY, bgcolor: alpha(GOLD, 0.08) } }}>Clear</Button>
          </Box>
        )}
      </Box>

      {/* ═══ TABLE ═══ */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 4px 12px rgba(11,30,45,0.06)" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", overflow: "auto", scrollBehavior: "smooth" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow sx={{
                "& th": {
                  background: `linear-gradient(180deg, ${alpha(NAVY, 0.06)} 0%, #f8fafc 100%)`,
                  // Solid fallback below the gradient — MUI sticky <th> needs an opaque
                  // background or rows show through while scrolling.
                  backgroundColor: "#f8fafc",
                  backdropFilter: "blur(6px)",
                  position: "sticky",
                  top: 0,
                  zIndex: 2,
                },
              }}>
                <TableCell sx={{ width: 40, py: 1.5, px: 1.5, borderBottom: `1px solid ${BORDER}` }}>
                  <Checkbox size="small" checked={selected.size === paged.length && paged.length > 0}
                    indeterminate={selected.size > 0 && selected.size < paged.length}
                    onChange={selectAll} sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18, color: alpha(NAVY, 0.35) }, "&.Mui-checked .MuiSvgIcon-root, &.MuiCheckbox-indeterminate .MuiSvgIcon-root": { color: GOLD } }} />
                </TableCell>
                {[
                  { label: "Restaurant", key: "name" as SortKey, sortable: true },
                  { label: "Contact", key: null, sortable: false },
                  { label: "Outstanding", key: "outstanding" as SortKey, sortable: true },
                  { label: "Orders", key: "order_count" as SortKey, sortable: true },
                  { label: "Credit", key: "credit_limit" as SortKey, sortable: true },
                  { label: "Status", key: null, sortable: false },
                  { label: "Actions", key: null, sortable: false, minWidth: 200 },
                ].map((col: any, i) => (
                  <TableCell key={i} align={col.align || "left"}
                    onClick={col.sortable ? () => toggleSort(col.key!) : undefined}
                    sx={{
                      fontSize: 11, fontWeight: 800, color: NAVY, py: 1.5, borderBottom: `1px solid ${BORDER}`,
                      cursor: col.sortable ? "pointer" : "default", textTransform: "uppercase", letterSpacing: "0.06em",
                      "&:hover": col.sortable ? { color: GOLD } : {},
                      whiteSpace: "nowrap",
                      ...(col.minWidth ? { minWidth: col.minWidth } : {}),
                    }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                      {col.label}{col.sortable && <SortIcon k={col.key!} />}
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <TableRow key={`sk-${i}`}>
                    <TableCell sx={{ py: 1.5, px: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Skeleton variant="rectangular" width={18} height={18} sx={{ borderRadius: 0.5 }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Skeleton variant="rounded" width={38} height={38} />
                        <Box>
                          <Skeleton width={140} height={14} />
                          <Skeleton width={90} height={10} />
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Skeleton width={120} height={14} />
                      <Skeleton width={80} height={10} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}><Skeleton width={70} /></TableCell>
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}><Skeleton width={50} /></TableCell>
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Skeleton width={60} />
                      <Skeleton variant="rectangular" width={50} height={4} sx={{ mt: 0.5, borderRadius: 1 }} />
                    </TableCell>
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}><Skeleton variant="rounded" width={60} height={22} /></TableCell>
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Skeleton variant="rounded" width={180} height={36} />
                    </TableCell>
                  </TableRow>
                ))
              ) : paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                    <GroupsIcon sx={{ fontSize: 44, color: "#e2e8f0", mb: 1 }} />
                    <Typography fontSize={14} color="#94a3b8" fontWeight={500}>
                      {statusFilter === "blocked"     ? "No blocked restaurants found"
                       : statusFilter === "overdue"     ? "No restaurants with overdue balances"
                       : statusFilter === "active"      ? "No active restaurants"
                       : statusFilter === "outstanding" ? "No restaurants with outstanding balance"
                       : "No restaurants found"}
                    </Typography>
                    <Typography fontSize={12} color="#cbd5e1" mb={2}>
                      {statusFilter === "all" ? "Try adjusting your search or add your first restaurant." : "Try clearing the filter or adjusting your search."}
                    </Typography>
                    <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
                      {statusFilter !== "all" && (
                        <Button size="small" variant="outlined" onClick={() => { setStatusFilter("all"); setPage(0); }}
                          sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, borderColor: BORDER, color: TEXT_SECONDARY, "&:hover": { borderColor: NAVY, color: NAVY } }}>
                          Clear filter
                        </Button>
                      )}
                      <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => navigate("/shop/create")}
                        sx={{ borderRadius: 2, textTransform: "none", fontSize: 12, borderColor: GOLD, color: NAVY, "&:hover": { borderColor: GOLD, bgcolor: alpha(GOLD, 0.1) } }}>Add Restaurant</Button>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : paged.map((row) => {
                const cs = row.credit_status;
                const statusLabel = cs?.has_overdue ? "Overdue" : cs?.is_blocked ? "Blocked" : cs?.color === "orange" ? "Near Limit" : "Active";
                const statusColor = cs?.has_overdue || cs?.is_blocked ? "#dc2626" : cs?.color === "orange" ? "#ea580c" : "#059669";
                const statusBg = cs?.has_overdue || cs?.is_blocked ? "#fef2f2" : cs?.color === "orange" ? "#fff7ed" : "#ecfdf5";
                const rids: number[] = typeof row.route_ids === "string" ? JSON.parse(row.route_ids || "[]") : (row.route_ids || []);
                const matchedRoutes = Array.isArray(rids) ? rids.map(rid => routes.find(r => r.id === rid)).filter(Boolean) : [];

                return (
                  <TableRow key={row.id} sx={{
                    transition: "background-color 0.2s ease",
                    "&:hover": { bgcolor: "#f9fafb" },
                    bgcolor: cs?.has_overdue ? "#fffbfa" : cs?.is_blocked ? "#fffbfa" : undefined,
                  }}>
                    <TableCell sx={{ py: 1.5, px: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Checkbox size="small" checked={selected.has(row.id)} onChange={() => toggleSelect(row.id)}
                        sx={{ p: 0, "& .MuiSvgIcon-root": { fontSize: 18, color: selected.has(row.id) ? GOLD : alpha(NAVY, 0.3) } }} />
                    </TableCell>

                    {/* Restaurant */}
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, cursor: "pointer" }} onClick={() => navigate(`/shop-history/${row.id}`)}>
                        <Avatar variant="rounded"
                          src={row.customerImg ? `${LARAVEL_URL}/${row.customerImg}` : undefined}
                          sx={{ width: 38, height: 38, borderRadius: 2.5, bgcolor: alpha(NAVY, 0.06), fontSize: 14, fontWeight: 800, color: NAVY, border: `1px solid ${alpha(GOLD, 0.3)}` }}>
                          {(row.customerCompanyName || row.customerFirstName || "R").charAt(0).toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography fontSize={13} fontWeight={700} color={TEXT_PRIMARY} sx={{ transition: "color 0.2s ease", "&:hover": { color: GOLD } }}>
                            {row.customerCompanyName || [row.customerFirstName, row.customerLastName].filter(Boolean).join(" ")}
                          </Typography>
                          {row.customerCity && (
                            <Typography fontSize={10} color={TEXT_SECONDARY}>{row.customerCity}{row.customerProvince ? `, ${row.customerProvince}` : ""}</Typography>
                          )}
                          {matchedRoutes.length > 0 && (
                            <Box sx={{ display: "flex", gap: 0.5, mt: 0.5 }}>
                              {matchedRoutes.slice(0, 2).map(r => r && (
                                <Chip key={r.id} label={r.name} size="small"
                                  sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: alpha(GOLD, 0.12), color: NAVY, borderRadius: 1, border: `1px solid ${alpha(GOLD, 0.3)}` }} />
                              ))}
                              {matchedRoutes.length > 2 && (
                                <Tooltip title={matchedRoutes.slice(2).map(r => r?.name).join(", ")}>
                                  <Chip label={`+${matchedRoutes.length - 2}`} size="small"
                                    sx={{ height: 16, fontSize: 8, fontWeight: 700, bgcolor: "#f1f5f9", color: "#94a3b8", borderRadius: 1 }} />
                                </Tooltip>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </TableCell>

                    {/* Contact */}
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Typography fontSize={12} color="#334155">{row.customerMobileNum || "—"}</Typography>
                      <Typography fontSize={10} color="#94a3b8" noWrap sx={{ maxWidth: 150 }}>{row.customerEmail || ""}</Typography>
                    </TableCell>

                    {/* Outstanding */}
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Typography fontSize={13} fontWeight={700} color={row.outstanding > 0 ? "#ea580c" : "#059669"}>
                        ${row.outstanding.toFixed(2)}
                      </Typography>
                    </TableCell>

                    {/* Orders */}
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Typography fontSize={13} fontWeight={700} color="#0f172a">{row.order_count}</Typography>
                      {row.last_order_date && (
                        <Typography fontSize={10} color="#94a3b8">{dayjs(row.last_order_date).format("MMM D")}</Typography>
                      )}
                    </TableCell>

                    {/* Credit */}
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      {cs?.credit_limit > 0 ? (
                        <Box>
                          <Typography fontSize={12} fontWeight={600} color="#334155">${cs.credit_limit.toLocaleString()}</Typography>
                          <Box sx={{ width: 50, height: 4, bgcolor: "#f1f5f9", borderRadius: 2, mt: 0.5 }}>
                            <Box sx={{
                              height: "100%", width: `${Math.min(100, cs.usage_percent)}%`, borderRadius: 2,
                              bgcolor: cs.color === "red" ? "#dc2626" : cs.color === "orange" ? "#ea580c" : "#059669",
                              transition: "width 0.3s",
                            }} />
                          </Box>
                        </Box>
                      ) : (
                        <Typography fontSize={11} color="#cbd5e1">No limit</Typography>
                      )}
                    </TableCell>

                    {/* Status */}
                    <TableCell sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9" }}>
                      <Chip label={statusLabel} size="small"
                        sx={{ fontSize: 10, fontWeight: 700, bgcolor: statusBg, color: statusColor, height: 22, borderRadius: 1.5,
                          border: `1px solid ${alpha(statusColor, 0.15)}` }} />
                    </TableCell>

                    {/* Actions */}
                    <TableCell align="left" sx={{ py: 1.5, borderBottom: "1px solid #f1f5f9", minWidth: 200 }}>
                      <Box
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "flex-start",
                          gap: "6px",
                          padding: "4px 8px",
                          borderRadius: "10px",
                          backgroundColor: "#f9fafb",
                        }}
                      >
                        {[
                          { tip: "View", icon: <VisibilityIcon />, click: () => navigate(`/shop-history/${row.id}`), color: GOLD, hoverBg: "#FFF7D6" },
                          { tip: "Invoices", icon: <ReceiptIcon />, click: () => navigate(`/shop-history/${row.id}`, { state: { tab: "Invoices" } }), color: NAVY, hoverBg: alpha(NAVY, 0.08) },
                          { tip: "Edit", icon: <EditIcon />, click: () => { setSelectedCoursing(row); setOpenDialog(true); }, color: "#1D4ED8", hoverBg: "#DBEAFE" },
                          { tip: cs?.is_blocked ? "Unblock" : "Block", icon: cs?.is_blocked ? <CheckCircleIcon /> : <BlockIcon />, click: () => handleBlockToggle(row.id, !cs?.is_blocked), color: cs?.is_blocked ? "#059669" : "#EF4444", hoverBg: cs?.is_blocked ? "#D1FAE5" : "#FEE2E2" },
                          { tip: "Delete", icon: <DeleteOutlineIcon />, click: () => { setCoursingToDelete(row); setOpenDeleteCategory(true); }, color: "#EF4444", hoverBg: "#FEE2E2" },
                        ].map((action, i) => (
                          <Tooltip key={i} title={action.tip} arrow>
                            <IconButton
                              size="small"
                              onClick={action.click}
                              sx={{
                                width: 28,
                                height: 28,
                                padding: "6px",
                                borderRadius: "8px",
                                color: action.color,
                                backgroundColor: "#ffffff",
                                border: `1px solid ${BORDER}`,
                                transition: "all 0.2s ease",
                                "&:hover": {
                                  backgroundColor: action.hoverBg,
                                  borderColor: alpha(action.color, 0.35),
                                  transform: "translateY(-1px)",
                                  boxShadow: `0 2px 6px ${alpha(action.color, 0.2)}`,
                                },
                              }}
                            >
                              {React.cloneElement(action.icon, { sx: { fontSize: 15 } })}
                            </IconButton>
                          </Tooltip>
                        ))}
                      </Box>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ px: 2, py: 1.5, borderTop: "1px solid #f1f5f9", display: "flex", justifyContent: "flex-end" }}>
          <Pagination count={sorted.length} page={page} rowsPerPage={rowsPerPage} onPageChange={(_: any, p: number) => setPage(p)} />
        </Box>
      </Box>

      {selectedCoursing && <NewPopUpCustomer openModel={openDialog} handleCloseDialog={() => { setSelectedCoursing(null); setOpenDialog(false); loadShops(); }} data={selectedCoursing} />}
      {coursingToDelete && (
        <DeletePopup open={openDeleteCategory} handleCloseDelete={() => { setCoursingToDelete(null); setOpenDeleteCategory(false); }}
          onConfirm={async () => { await handleDelete(coursingToDelete.id.toString()); }}
          title="Delete Restaurant" content={`Are you sure you want to delete "${coursingToDelete.customerCompanyName || [coursingToDelete.customerFirstName, coursingToDelete.customerLastName].filter(Boolean).join(" ")}"?`} />
      )}
    </Box>
  );
};

export default Index;
